import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Entity } from '../../classes/entity.class';
import { ApiResponse, ApiService } from '../api/api.service';
import { RealtimeType } from '../realtime/realtime-type.enum';
import { RealtimeService } from '../realtime/realtime.service';
import { WebsocketService } from '../websocket/websocket.service';
import { EntityType } from './entity-type.class';

@Injectable({
    providedIn: 'root',
})
export class EntitiesService extends RealtimeService<Entity> {
    private _types$: Observable<ApiResponse<EntityType[]>>;

    constructor(
        protected apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, RealtimeType.entities);
    }

    public itemInDaterange(entity: Entity, fromDate: Date, toDate: Date) {
        const fromTime = fromDate.getTime();
        const toTime = toDate.getTime();
        return (
            new Date(entity.begindate).getTime() < toTime &&
            (new Date(entity.enddate).getTime() >= fromTime || entity.enddate == null)
        );
    }

    public get(id: number): Promise<Entity> {
        return this.apiService.getCall(`${this.type}/${id}`);
    }

    public getByType(code: string | string[]): Observable<Entity[]> {
        return new Observable(observer => {
            this.getList().subscribe(entities => {
                observer.next(
                    entities.filter(entity => {
                        if (Array.isArray(code)) {
                            return (
                                entity.entitytypes
                                    .map(et => et.key)
                                    .filter(etKey => code.map(c => String(c)).includes(etKey))?.length > 0
                            );
                        } else {
                            return entity.entitytypes.map(et => et.key).includes(code);
                        }
                    })
                );
            });
        });
    }

    public getList(beginDate?: Date, endDate?: Date, withOutOfServiceEntityIds?: number[]): Observable<Entity[]> {
        return combineLatest([super.getList(), this.getTypesMap()])
            .pipe(
                map(([list, typeMap]) => {
                    list = list.map(entity => this.applyEntityTypes(entity, typeMap));

                    if (beginDate) {
                        if (!endDate) {
                            endDate = beginDate;
                        }
                        return list.filter(entity => {
                            const notOut =
                                new Date(entity.begindate).getTime() < new Date(beginDate).getTime() &&
                                (!entity.enddate || new Date(entity.enddate).getTime() > new Date(endDate).getTime());
                            entity.outOfService = withOutOfServiceEntityIds?.includes(entity.id) && !notOut;
                            return withOutOfServiceEntityIds?.includes(entity.id) || notOut;
                        });
                    }
                    list = list.sort((a, b) => {
                        const getSorter = (entity: Entity) =>
                            Math.max(...entity.entitytypes.map(et => +(et.pivot?.order ?? 99999)));
                        return getSorter(a) - getSorter(b);
                    });
                    return list.filter(l => l.entitytypes?.length > 0);
                })
            )
            .pipe(
                map(a =>
                    a.sort((a, b) => {
                        const getSorter = (entity: Entity) => (entity.use_once ? 0 : 1000);
                        return getSorter(a) - getSorter(b);
                    })
                )
            );
    }

    public getMap(): Observable<Map<number, Entity>> {
        return new Observable(observer => {
            this.getList().subscribe((entities: Entity[]) => {
                observer.next(this.genEntitiesMap(entities));
            });
        });
    }

    public getUnavailableEntityIds(planningId: number, beginDate: Date, endDate: Date): Promise<number[]> {
        return this.apiService.postCall(`${this.type}/not-available`, { planningId, beginDate, endDate });
    }

    public save(entity: Entity): Promise<Entity> {
        return this.apiService.postCall<Entity>(this.type, entity);
    }

    public changeOrder(entities: { order; entity_id; entitytype_key }[], entitytype_key: string): Promise<boolean> {
        return this.apiService.postCall(`${this.type}/change-order`, {
            entities,
            entitytype_key,
        });
    }

    public getTypes() {
        return (this._types$ ??= this.apiService.getCall$<EntityType[]>('entities/types').pipe(shareReplay()));
    }

    public getTypesMap(): Observable<Map<string, EntityType>> {
        return this.getTypes().pipe(
            map(types => {
                const typeMap = new Map<string, EntityType>();
                types.data.forEach(type => {
                    typeMap.set(type.key, type);
                });
                return typeMap;
            })
        );
    }

    private genEntitiesMap(entities: Entity[]): Map<number, Entity> {
        const map = new Map<number, Entity>();
        entities.forEach(entity => {
            map.set(entity.id, entity);
        });
        return map;
    }

    private applyEntityTypes(entity: Entity, typeMap: Map<string, EntityType>): Entity {
        const keys = entity.entitytype_keys ?? entity.entitytypes?.map(entityType => String(entityType.key)) ?? [];

        entity.entitytypes = keys
            .map(key => {
                const type = typeMap.get(key);

                const entityType: EntityType | null = type
                    ? {
                          ...type,
                          pivot: {
                              order: entity.entitytype_order?.[key] ?? type.pivot?.order ?? 99999,
                          },
                      }
                    : null;

                return entityType;
            })
            .filter((type): type is EntityType => !!type);

        return entity;
    }
}
